import { cartParams } from '@/helpers/BuyMembershipHelpers';
import { getMembershipConfigProperty } from '@/helpers/Config';
import { mapEntries } from '@/helpers/DictHelpers';
import { environment, timezone } from '@/helpers/Environment';
import { groupBy, indexBy } from '@/helpers/IndexHelpers';
import { TixTime } from '@/TixTime/TixTime';
import { reserveTickets } from '@/state/Reserve';
import store from '@/store/store';
/**
 * @deprecated This doesn't support multi-memberships
 */
export function currentMembership() {
    const now = new TixTime(undefined, timezone);
    return membershipOnDate(now);
}
/**
 * @deprecated This doesn't support multi-memberships
 */
export function membershipOnDate(date) {
    const memberships = allMemberships();
    return findMembershipOnDate(memberships, date);
}
/**
 * @deprecated This doesn't support multi-memberships
 */
export function purchasedMembershipOnDate(date) {
    const memberships = purchasedMemberships();
    return findMembershipOnDate(memberships, date);
}
function findMembershipOnDate(memberships, date) {
    return memberships.find((membership) => {
        const from = membership.validFrom;
        const to = membership.validTo;
        return date.isBetween(from, to, 'day', '[]');
    });
}
export function inCartMembership() {
    const gifting = store.getters['Cart/giftee'];
    const items = store.getters['Cart/cartItems'];
    const item = items.find((item) => item.isMembershipEvent);
    if (item && !gifting) {
        return cartItemToMembership(item);
    }
}
/**
 * @deprecated Just use the store.
 */
export function purchasedMemberships() {
    return store.getters['Member/membershipsWithSummaries'];
}
/**
 * Includes the most recent membership from each membership program, including expired ones.
 */
export function mostRecentMemberships() {
    const memberships = store.getters['Member/memberships'];
    const result = {};
    for (const membership of memberships) {
        const now = new TixTime(null, timezone);
        // Ignore future memberships
        if (membership.validFrom.isAfter(now))
            continue;
        const programId = membership.entity.membership_program_id ?? 'default';
        // Get the most recent membership for each membership program
        if (!result[programId] || membership.validTo.isAfter(result[programId].validTo)) {
            result[programId] = membership;
        }
    }
    return Object.values(result);
}
/**
 * Includes the _active_ membership from each membership program.
 *
 * Expired memberships are excluded.
 */
export function currentMemberships() {
    const memberships = store.getters['Member/memberships'];
    const now = new TixTime(undefined, timezone);
    return memberships.filter((membership) => {
        return now.isBefore(membership.validTo) && now.isAfter(membership.validFrom);
    });
}
/**
 * @deprecated This doesn't support multi-memberships
 */
export function hasCurrentPurchasedMembership() {
    const now = new TixTime(undefined, timezone);
    return purchasedMembershipOnDate(now) ? true : false;
}
export function isCurrentMember() {
    return currentMemberships().length > 0;
}
export function isCurrentOrPastMember() {
    const memberships = purchasedMemberships();
    return memberships.length > 0;
}
function allMemberships() {
    const inCart = inCartMembership();
    // Always prioritise an in-cart membership over any purchased memberships so that the customer can not unnecessarily
    // purchase a membership in order to get benefits that they already have.
    return inCart ? [inCart] : purchasedMemberships();
}
function cartItemToMembership(cartItem) {
    const rules = membershipRules();
    if (rules) {
        const limits = {};
        for (const lineItem of cartItem.types) {
            // cartItem can contain ticket types where type.group.handler === 'codes' which are not relevant to member benefits.
            if (lineItem.group.handler === 'membership') {
                const name = rules[lineItem.group.id][lineItem.type.id].member_event_ticket_type;
                if (limits[name]) {
                    limits[name] += lineItem.ticketCount;
                }
                else {
                    limits[name] = lineItem.ticketCount;
                }
            }
        }
        return {
            benefits: limits,
            validTo: cartItem.validTo,
            validFrom: cartItem.validFrom,
        };
    }
}
function membershipRules() {
    const rules = store.state['membershipRules'];
    if (rules.length) {
        const byGroup = groupBy('ticket_group_id', rules);
        return mapEntries(byGroup, (group) => indexBy('ticket_type_id', group));
    }
}
export function addMembershipToCart(event, membershipRules, selectedLevel, promoCodes, tickets, surveyAnswers, autoRenew, codes) {
    store.commit('Cart/autoRenewMembership', autoRenew);
    // Ensure the membershipRules are available to create a membership entity from an in-cart membership.
    // @see cartItemToMembership()
    store.commit('membershipRules', membershipRules.rules);
    const changes = cartParams(event, selectedLevel, surveyAnswers, promoCodes.filter((code) => code != null));
    changes.add = tickets;
    if (codes) {
        changes.preReservePromoCodes = { codes };
    }
    return reserveTickets(changes);
}
export function allowMemberReserveBeyondTerm() {
    const reserveBeyondTerm = environment.config.member_reserve_session_beyond_term;
    const autoRenew = store.state['Cart'].autoRenewMembership;
    return reserveBeyondTerm === 'always' || (reserveBeyondTerm === 'if_autorenew' && autoRenew);
}
export function membershipIsRenewable(membership, group, event) {
    const allowedDate = membershipRenewableFrom(membership, group, event);
    const now = new TixTime(null, timezone);
    return allowedDate.isSameOrBefore(now);
}
export function membershipRenewableFrom(membership, group, event) {
    const config = getMembershipConfigProperty('renewal', 'allowed_period', group, event) ?? null;
    if (config !== null) {
        return membership.validTo.subtract(config, 'days');
    }
    // Membership is renewable from today if the config is not set
    return new TixTime(null, timezone);
}
